import { zodResolver } from "@hookform/resolvers/zod";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { format } from "date-fns";
import { Info, MinusIcon, PlusIcon, X } from "lucide-react";
import Link from "next/link";
import * as React from "react";
import { useForm } from "react-hook-form";
import * as z from "zod";

import { OverlayCta } from "@/components/cta";
import { Grade } from "@/components/edb";
import parse from "@/components/html-parser";
import Filter from "@/components/icons/filter";
import { Loading } from "@/components/icons/loading";
import { LockBadge, LockBadgeOverlay } from "@/components/lock";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from "@/components/ui/form";
import {
	Select,
	SelectContent,
	SelectGroup,
	SelectItem,
	SelectLabel,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import {
	Sheet,
	SheetClose,
	SheetContent,
	SheetDescription,
	SheetHeader,
	SheetTitle,
	SheetTrigger,
} from "@/components/ui/sheet";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { fetchWrapper } from "@/lib/hooks/fetch-client";
import { useAccess } from "@/lib/hooks/use-access";
import { cn } from "@/lib/utils";
import { handleGradeInfo } from "@/lib/utils/index";
import { AccessRules } from "@/types";

const StudySidebarTrigger = ({ children, ...props }: React.HTMLAttributes<HTMLDivElement>) =>
	children;

interface StudyDetails {
	ages: string[];
	bodies: string[];
	change: string;
	duration: string;
	effect: string;
	funding: string;
	gender: string;
	notes: string;
	publication_date: string | null;
	samplesize: number;
	study_url: string;
	title: string;
	trial_design: string;
	values: any; // You can replace 'any' with a more specific type if needed
}

type SortOption = {
	text: string;
	value: string;
};

const sortOptions: SortOption[] = [
	{ text: "Alphabetical (Ascending)", value: "alpha" },
	{ text: "Publish date (default - Descending)", value: "publish" },
	{ text: "Participants (Descending)", value: "participant" },
];

type FilterOption = {
	text: string;
	value: string;
};

const filterOptions: FilterOption[] = [
	{ text: "Show All", value: "all" },
	{ text: "Contains Study Summary", value: "with-summary" },
];

type AgeOption = {
	text: string;
	value: string;
};

const ageOptions: AgeOption[] = [
	{ text: "All", value: "all" },
	{ text: "1-6", value: "1-6" },
	{ text: "7-12", value: "7-12" },
	{ text: "13-17", value: "13-17" },
	{ text: "18-29", value: "18-29" },
	{ text: "30-44", value: "30-44" },
	{ text: "45-64", value: "45-64" },
	{ text: "65+", value: "65" },
];

type SexOption = {
	text: string;
	value: string;
};

const sexOptions: SexOption[] = [
	{ text: "All", value: "all" },
	{ text: "Female", value: "Female" },
	{ text: "Male", value: "Male" },
	{ text: "Only Both", value: "Both Genders" },
];

type DefaultFilters = {
	sort: SortOption;
	filter: FilterOption;
	age: string[];
	sex: string[];
};

const defaultFilters: DefaultFilters = {
	sort: sortOptions[1],
	filter: filterOptions[0],
	age: ageOptions.map(({ value }) => value),
	sex: sexOptions.map(({ value }) => value),
};

const FormSchema = z.object({
	sort: z.string(),
	filter: z.string(),
	age: z.array(z.string()).refine((value) => value.some((item) => item), {}),
	sex: z.array(z.string()).refine((value) => value.some((item) => item), {}),
});

interface StudySidebarFilterProps extends React.HTMLAttributes<HTMLDivElement> {
	toggle: boolean;
	handleFilter: (data: z.infer<typeof FormSchema>) => void;
}

const StudySidebarFilter = ({
	toggle,
	children,
	handleFilter,
	...props
}: StudySidebarFilterProps) => {
	const [open, setOpen] = React.useState<boolean>(false);

	React.useEffect(() => {
		setOpen(toggle);
	}, [toggle]);

	const form = useForm<z.infer<typeof FormSchema>>({
		resolver: zodResolver(FormSchema),
		defaultValues: {
			sort: defaultFilters.sort.value,
			filter: defaultFilters.filter.value,
			age: defaultFilters.age,
			sex: defaultFilters.sex,
		},
	});

	function onSubmit(data: z.infer<typeof FormSchema>) {
		handleFilter(data);
		setOpen(false);
	}

	return (
		<div
			className={cn(
				"absolute top-24 -mx-6 w-full bg-main px-6 shadow-2xl transition duration-300 ease-in-out",
				open ? "translate-x-0" : "translate-x-full"
			)}
		>
			<Form {...form}>
				<form onSubmit={form.handleSubmit(onSubmit)} className="space-y-5 rounded-l-lg py-4">
					<div className="grid grid-cols-6 items-center justify-between">
						<h6 className="col-span-3 font-bold">Advanced Filters</h6>
						<Button
							onClick={(e) => {
								e.preventDefault();
								form.reset();
							}}
							variant="text"
							size="md"
							className="col-span-2"
						>
							Reset Filters
						</Button>
						<Button
							variant="text"
							size="sm"
							className="justify-self-end px-0"
							onClick={() => setOpen(!open)}
						>
							<svg
								width="25"
								height="25"
								viewBox="0 0 25 25"
								fill="purple"
								xmlns="http://www.w3.org/2000/svg"
							>
								<path
									d="M12.7842 22.5C18.307 22.5 22.7842 18.0228 22.7842 12.5C22.7842 6.97715 18.307 2.5 12.7842 2.5C7.26133 2.5 2.78418 6.97715 2.78418 12.5C2.78418 18.0228 7.26133 22.5 12.7842 22.5Z"
									stroke="white"
									strokeWidth="2"
									strokeLinecap="round"
									strokeLinejoin="round"
								/>
								<path
									d="M15.7842 9.5L9.78418 15.5"
									stroke="white"
									strokeWidth="2"
									strokeLinecap="round"
									strokeLinejoin="round"
								/>
								<path
									d="M9.78418 9.5L15.7842 15.5"
									stroke="white"
									strokeWidth="2"
									strokeLinecap="round"
									strokeLinejoin="round"
								/>
							</svg>
						</Button>
					</div>
					{/* SORT BY */}
					<FormField
						control={form.control}
						name="sort"
						render={({ field }) => (
							<FormItem>
								<SelectGroup>
									<SelectLabel className="px-0 py-1.5 font-lora text-lg font-medium">
										Sort By:
									</SelectLabel>
									<Select onValueChange={field.onChange} value={field.value}>
										<SelectTrigger>
											<SelectValue placeholder={defaultFilters.sort.text} />
										</SelectTrigger>
										<SelectContent className="z-1000" sideOffset={4}>
											{sortOptions.map((option: SortOption) => (
												<SelectItem key={option.value} value={option.value}>
													{option.text}
												</SelectItem>
											))}
										</SelectContent>
									</Select>
								</SelectGroup>
							</FormItem>
						)}
					/>
					{/* AGE FILTER */}
					<FormField
						control={form.control}
						name="age"
						render={() => (
							<FormItem>
								<FormLabel className="px-0 py-1.5 font-lora text-lg font-medium">Age:</FormLabel>
								<div className="grid grid-cols-3 gap-4 py-2">
									{ageOptions.map((option: AgeOption) => (
										<div key={option.value} className="flex items-center justify-start gap-2">
											<FormField
												control={form.control}
												name="age"
												render={({ field }) => {
													return (
														<FormItem
															key={option.value}
															className="flex flex-row items-center space-x-3 space-y-0"
														>
															<FormControl>
																<Checkbox
																	checked={field.value?.includes(option.value)}
																	onCheckedChange={(checked) =>
																		option.value === "all"
																			? field.onChange(
																					checked ? [...ageOptions.map(({ value }) => value)] : []
																			  )
																			: field.onChange(
																					checked
																						? [...field.value, option.value]
																						: field.value?.filter((value) => value !== option.value)
																			  )
																	}
																/>
															</FormControl>
															<FormLabel className="leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
																{option.text}
															</FormLabel>
														</FormItem>
													);
												}}
											/>
										</div>
									))}
								</div>
								<FormMessage />
							</FormItem>
						)}
					/>
					{/* SEX FILTER */}
					<FormField
						control={form.control}
						name="sex"
						render={() => (
							<FormItem>
								<FormLabel className="px-0 py-1.5 font-lora text-lg">Sex:</FormLabel>
								<div className="grid grid-cols-3 gap-4 py-2">
									{sexOptions.map((option: AgeOption) => (
										<div className="flex items-center justify-start gap-2" key={option.value}>
											<FormField
												control={form.control}
												name="sex"
												render={({ field }) => {
													return (
														<FormItem
															key={option.value}
															className="flex flex-row items-center space-x-3 space-y-0"
														>
															<FormControl>
																<Checkbox
																	checked={field.value?.includes(option.value)}
																	onCheckedChange={(checked) =>
																		option.value === "all"
																			? field.onChange(
																					checked ? [...sexOptions.map(({ value }) => value)] : []
																			  )
																			: field.onChange(
																					checked
																						? [...field.value, option.value]
																						: field.value?.filter((value) => value !== option.value)
																			  )
																	}
																/>
															</FormControl>
															<FormLabel className="leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
																{option.text}
															</FormLabel>
														</FormItem>
													);
												}}
											/>
										</div>
									))}
								</div>
								<FormMessage />
							</FormItem>
						)}
					/>

					<div className="flex justify-end">
						<Button type="submit" size="sm">
							Apply
						</Button>
					</div>
				</form>
			</Form>
		</div>
	);
};

interface StudyCardProps extends React.HTMLAttributes<HTMLDivElement> {
	study: StudyDetails;
	showContent?: boolean;
	hideEffect?: boolean;
}

const StudyCard = ({ study, showContent, hideEffect, ...props }: StudyCardProps) => {
	const [open, setOpen] = React.useState<boolean>(false);

	return (
		<Collapsible open={open} onOpenChange={setOpen}>
			<CollapsibleTrigger asChild>
				<div className="grid items-start gap-4 md:flex">
					<Link
						href={study.study_url}
						target="_blank"
						className=" font-medium leading-7 -tracking-2 text-primary hover:underline"
					>
						{study.title}
					</Link>

					<Button
						className={cn(
							"flex items-center justify-center gap-1 whitespace-nowrap rounded border border-primary p-2 text-sm tracking-1 md:py-1 lg:hover:bg-primary lg:hover:text-white",
							open ? "bg-primary text-white" : "bg-transparent text-primary"
						)}
					>
						<span className="block">Study details</span>

						{open ? <MinusIcon className="size-3" /> : <PlusIcon className="size-3" />}
					</Button>
				</div>
			</CollapsibleTrigger>
			<CollapsibleContent>
				<div className="py-3">
					{study?.notes && (
						<Alert variant="summary" className="my-3 md:text-base">
							<AlertTitle className="text-lg font-semibold leading-6">Study Summary</AlertTitle>
							<AlertDescription className="relative">
								{!showContent && (
									<LockBadgeOverlay
										size={10}
										className="w-3/4 px-4 py-2"
										text={<OverlayCta trackEdbSignup />}
									/>
								)}

								<div className={cn(!showContent && "text-gray-500 blur-sm", "space-y-2")}>
									{showContent
										? parse(study?.notes)
										: "Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt " +
										  "ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco "}
								</div>
							</AlertDescription>
						</Alert>
					)}
					<div className="pt-3">
						<div className="divide-y divide-gray-300 text-menu -tracking-2">
							{!hideEffect && study?.change && (
								<div className="grid grid-cols-2 py-2">
									<p className="font-semibold leading-9">Effect</p>
									<p className={`pt-2 text-primary ${!showContent && "blur-sm"}`}>
										{showContent ? study?.change : "BLOCKED"}
									</p>
								</div>
							)}
							{study?.trial_design && (
								<div className="grid grid-cols-2 py-2">
									<p className="font-semibold leading-9">Trial Design</p>
									<p className={`pt-2 text-primary ${!showContent && "blur-sm"}`}>
										{showContent ? study?.trial_design : "BLOCKED"}
									</p>
								</div>
							)}
							{study?.duration && (
								<div className="grid grid-cols-2 py-2">
									<p className="font-semibold leading-9">Trial Length</p>
									<p className={`pt-2 text-primary ${!showContent && "blur-sm"}`}>
										{showContent ? study?.duration : "BLOCKED"}
									</p>
								</div>
							)}
							{study?.samplesize && (
								<div className="grid grid-cols-2 py-2">
									<p className="font-semibold leading-9">Number of Participants</p>
									<p className={`pt-2 text-primary ${!showContent && "blur-sm"}`}>
										{showContent ? study?.samplesize : "BLOCKED"}
									</p>
								</div>
							)}
							{study?.gender && (
								<div className="grid grid-cols-2 py-2">
									<p className="font-semibold leading-9">Sex</p>
									<p className={`pt-2 text-primary ${!showContent && "blur-sm"}`}>
										{showContent ? study?.gender.replace("Genders", "sexes") : "BLOCKED"}
									</p>
								</div>
							)}
							{study?.ages && study?.ages.length && (
								<div className="grid grid-cols-2 py-2">
									<p className="font-semibold leading-9">Age Range</p>
									<p className={`pt-2 text-primary ${!showContent && "blur-sm"}`}>
										{showContent
											? study?.ages?.map((type, idx) => (
													<span key={idx}>
														{(idx ? ",  " : "") + type + (type === "65" ? "+" : "")}
													</span>
											  ))
											: "BLOCKED"}
									</p>
								</div>
							)}
							{study?.bodies && study?.bodies.length && (
								<div className="grid grid-cols-2 py-2">
									<p className="font-semibold leading-9">Body Types</p>
									<p className={`pt-2 text-primary ${!showContent && "blur-sm"}`}>
										{showContent
											? study?.bodies?.map((type, idx) => (
													<span key={idx} className="capitalize">
														{(idx ? ",  " : "") + type}
													</span>
											  ))
											: "BLOCKED"}
									</p>
								</div>
							)}
							{study?.publication_date && (
								<div className="grid grid-cols-2 py-2">
									<p className="font-semibold leading-9">Publication Date</p>
									<p className={`pt-2 text-primary ${!showContent && "blur-sm"}`}>
										{showContent
											? format(new Date(study?.publication_date), "MMMM d',' yyyy")
											: "BLOCKED"}
									</p>
								</div>
							)}
						</div>
					</div>
					{study?.funding && typeof study?.funding === "string" && (
						<>
							<p className="mb-2 mt-5 font-lora text-lg font-semibold text-gray-900 lg:text-xl">
								Funding notes for this study:
							</p>
							<div
								className={cn(
									"relative space-y-6 overflow-hidden rounded-xl bg-gray-100 px-9 py-6 leading-7 -tracking-2",
									"before:absolute before:left-0 before:top-0 before:h-full before:w-2 before:bg-examine-purple-300",
									showContent ? "text-gray-900" : "text-gray-500 blur-sm"
								)}
							>
								{showContent
									? parse(study?.funding)
									: "Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt " +
									  "ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco "}
							</div>
						</>
					)}
				</div>
			</CollapsibleContent>
		</Collapsible>
	);
};

interface StudySidebarProps extends React.HTMLAttributes<HTMLDivElement> {
	page?: { name: string; slug: string };
	parent?: any;
	entry?: any;
	accessRules: AccessRules;
	children: React.ReactNode;
}

const StudySidebar = ({ page, parent, entry, accessRules, children }: StudySidebarProps) => {
	const [studies, setStudies] = React.useState<StudyDetails[]>([]);
	const [, setStudiesData] = React.useState(null);
	const [loading, setLoading] = React.useState<boolean>(true);
	const [title, setTitle] = React.useState<string>("");
	const [toggleFilter, setToggleFilter] = React.useState<boolean>(false);

	const hasAccess = useAccess("examine_database");

	const endpoint = () => {
		if (parent?.intervention?.slug && parent?.intervention?.type === "no-parent") {
			return `/v1/interventions/${page?.slug}/study-database/${entry?.slug}/?all=1`;
		}

		if (parent?.topic?.slug && parent?.topic?.type === "no-parent") {
			return `/v1/topics/${page?.slug}/study-database/${entry?.slug}`;
		}

		if (parent?.intervention?.slug) {
			if (entry?.slug) {
				return `/v1/topics/${page?.slug}/study-database/${parent?.intervention?.slug}/${entry?.slug}`;
			}

			return `/v1/topics/${page?.slug}/study-database/${parent?.intervention?.slug}`;
		}

		if (parent?.condition?.slug) {
			if (entry?.slug) {
				return `/v1/interventions/${page?.slug}/study-database/${entry?.slug}/${parent?.condition?.slug}`;
			}

			return `/v1/interventions/${page?.slug}/study-database/${parent?.condition?.slug}?condition=1`;
		}
	};

	const getStudies = async () => {
		try {
			const data: any = await fetchWrapper(endpoint());

			return data || null;
		} catch (error) {
		} finally {
			setLoading(false);
		}
	};

	const initializeStudies = async () => {
		const data: any = await getStudies();

		const { studies, total_studies = 0 } = data;

		setStudies(studies);
		setStudiesData(data);
		setTitle(`${total_studies} ${+total_studies > 1 ? "Studies" : "Study"} Found`);
	};

	const filterSDDetails = (
		filters: z.infer<typeof FormSchema>,
		studies: StudyDetails[]
	): StudyDetails[] => {
		if (filters.sort) {
			switch (filters.sort) {
				case "publish":
					// TODO: Handle 'publish' case
					break;
				case "alpha":
					studies.sort((a, b) => {
						const titleA = a.title.toLowerCase();
						const titleB = b.title.toLowerCase();

						if (titleA < titleB) return -1;
						if (titleA > titleB) return 1;
						return 0;
					});
					break;
				case "participant":
					studies.sort((a, b) => b.samplesize - a.samplesize);
					break;
			}
		}

		if (filters.age) {
			studies = studies.filter((study) => {
				return (
					filters.age.filter((value) => {
						if (value === "all") {
							return !study.ages;
						}
						return study.ages?.includes(value) ?? false;
					}).length > 0
				);
			});
		}

		if (filters.sex) {
			if (filters.sex.includes("all") && !filters.sex.includes("N/A")) {
				filters.sex.push("N/A");
			}

			studies = studies.filter((study) => {
				return filters.sex.includes(study.gender);
			});
		}

		if (filters.filter) {
			switch (filters.filter) {
				case "with-summary":
					studies = studies.filter((study) => {
						return !!study.notes;
					});
					break;
			}
		}

		return studies;
	};

	const handleFilter = async (data: z.infer<typeof FormSchema>) => {
		const { studies, total_studies } = await getStudies();

		const filteredStudies = filterSDDetails(data, studies);
		setStudies(filteredStudies);

		if (filteredStudies.length === studies.length) {
			setTitle(`${total_studies} ${+total_studies > 1 ? "Studies" : "Study"} Found`);
			return;
		}

		setTitle(`${filteredStudies.length} of ${total_studies} Studies Shown`);
	};

	return (
		<Sheet>
			<SheetTrigger
				asChild
				onClick={(e) => {
					e.stopPropagation();
					initializeStudies();
				}}
			>
				{children}
			</SheetTrigger>
			<SheetContent
				side={"right"}
				className="overflow-x-hidden pt-0"
				onClick={(e) => e.stopPropagation()}
			>
				<SheetHeader className="flex flex-row items-center justify-between space-y-0 py-3">
					<SheetTitle>{title}</SheetTitle>
					<div className="flex items-center gap-2">
						<Button onClick={() => setToggleFilter((currentValue) => !currentValue)}>
							<Filter />
							<span className="text-sm tracking-1">Filters</span>
						</Button>
						<SheetClose asChild>
							<Button variant="text" aria-label="Close">
								<X size={26} />
							</Button>
						</SheetClose>
					</div>
					<VisuallyHidden>
						<SheetDescription>{title}</SheetDescription>
					</VisuallyHidden>
				</SheetHeader>

				{loading ? (
					<div className="grid h-full place-content-center">
						<Loading />
					</div>
				) : (
					<>
						{entry?.grade && (
							<div className="flex items-center rounded-lg border border-examine-yellow-600 bg-examine-yellow-400 p-2.5">
								{entry?.showContent ? (
									<Grade entry={entry} />
								) : (
									<Link href="/plus">
										<LockBadge size={10} />
									</Link>
								)}
								<p className="ml-3 font-medium">
									{entry?.showContent ? entry?.grade_label : "Locked"}
								</p>
								{entry?.showContent && (
									<div className="ml-auto">
										<Tooltip>
											<TooltipTrigger>
												<Info size={20} strokeWidth={1.5} />
											</TooltipTrigger>
											<TooltipContent>
												<p>{handleGradeInfo(entry?.grade)}</p>
											</TooltipContent>
										</Tooltip>
									</div>
								)}
							</div>
						)}

						{entry?.notes && (
							<Alert variant="summary" className="my-3 md:text-base">
								<AlertTitle className="text-lg font-semibold leading-6">
									Summary of {page?.name}
								</AlertTitle>
								<AlertDescription className="relative">
									<p
										className={cn(
											"relative mt-2 text-menu leading-6 -tracking-2 text-gray-900",
											!entry?.showTooltip && "text-gray-500 blur-sm"
										)}
									>
										{entry?.showTooltip
											? parse(entry?.notes)
											: "Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt " +
											  "ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco " +
											  "laboris nisi ut aliquip ex ea commodo consequat. Duis aute irure dolor in reprehenderit in voluptate "}
									</p>

									{!entry?.showTooltip && (
										<LockBadgeOverlay
											size={10}
											className="w-3/4 px-4 py-2"
											text={<OverlayCta trackEdbSignup />}
										/>
									)}
								</AlertDescription>
							</Alert>
						)}

						<ul className="divide-y divide-gray-300">
							{studies?.map((study: StudyDetails, id) => (
								<li className="pb-2 pt-3" key={study.title}>
									<StudyCard study={study} showContent={hasAccess || id < 1} />
								</li>
							))}

							{!studies?.length ? <li className="text-center italic">No studies found.</li> : null}
						</ul>

						<StudySidebarFilter toggle={toggleFilter} handleFilter={handleFilter} />
					</>
				)}
			</SheetContent>
		</Sheet>
	);
};

export { StudySidebarTrigger, StudySidebar };
